import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { VerificationType } from '../../../globals';
import { getEnumString } from '../../enum-helper';

@Component({
  selector: 'ez-verification-code',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './verification-code.component.html',
  styleUrls: ['./verification-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationCodeComponent {
  verificationTypeEnum = VerificationType;

  isInvalid: boolean;
  errorMessage: string;
  verificationType: VerificationType;
  display: string;
  showTrustComputer: boolean;

  form: FormGroup;

  get isAuthenticator(): boolean {
    return this.verificationType === VerificationType.AUTHENTICATOR;
  }

  get verificationTypeString(): string {
    return getEnumString(this.verificationType, VerificationType) ?? '';
  }

  readonly #cdRef = inject(ChangeDetectorRef);

  constructor(elementRef: ElementRef) {
    const getAttribute = elementRef.nativeElement.getAttribute.bind(elementRef.nativeElement);

    this.isInvalid = getAttribute('is-invalid') === 'is-invalid';
    this.errorMessage = getAttribute('error-message');
    this.verificationType = VerificationType[getAttribute('verification-type') as keyof typeof VerificationType];
    this.display = getAttribute('display');
    this.showTrustComputer = getAttribute('show-trust-computer') === 'show-trust-computer';

    if (this.showTrustComputer) {
      this.form = new FormGroup({
        verificationCode: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')]),
        trustThisComputer: new FormControl(true),
      });
    } else {
      this.form = new FormGroup({
        verificationCode: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')]),
      });
    }

    if (this.isInvalid) {
      this.form.controls['verificationCode'].setErrors({ invalid: true });
    }
  }

  submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = new FormData();

    formData.append('VerificationCode', this.form.value.verificationCode);
    formData.append('VerificationType', getEnumString(this.verificationType, VerificationType) ?? '');

    if (this.form.controls['trustThisComputer']) {
      formData.append('TrustThisComputer', this.form.value.trustThisComputer);
    }

    fetch('', { method: 'POST', body: formData }).then(res => {
      if (res.redirected) {
        window.location.href = res.url;
        return;
      }

      // Since this is effectively a post-back call on the MVC page, the response
      // is the entire HTML of the page.
      // This locates the updated <ez-verification-code element and captures the
      // error-message and is-invalid attributes (the only ones that would change).
      res.body?.getReader().read().then(({ value }) => {
        const pageContent = new TextDecoder('utf-8').decode(value);
        const match = pageContent.match(/<ez-verification-code (?:(?:"(?:[^"\\]|\\.)*")|[^>"])+>/g);

        if (match) {
          const errorMessageMatch = match[0].match(/error-message="(.*)"/);

          this.errorMessage = errorMessageMatch ? errorMessageMatch[1] : '';
          this.isInvalid = match[0].indexOf('is-invalid') !== -1;

          this.#cdRef.detectChanges();
        }
      })
    });
  }
}
